import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { PhoneOff, Mic, MicOff, AlertCircle, Share2, Copy, CheckCircle } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXTwitter, faFacebook, faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { LiveTranscription } from './LiveTranscription';
import { RecordingLoadingPage } from './RecordingLoadingPage';
import { RecordingPage } from './RecordingPage';
import { analytics, trackClick, trackSocialShare } from '../services/analytics';

const Container = styled.div`
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
`;

const CallContainer = styled.div`
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%);
  backdrop-filter: blur(24px);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 20px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 20px 40px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2);
`;

const CallHeader = styled.div`
  text-align: center;
  margin-bottom: 20px;
`;

const CallStatus = styled.h2`
  color: white;
  font-size: 20px;
  margin: 0 0 8px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-weight: 600;
`;

const CallTimer = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const CallInfo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
`;

const CallId = styled.span`
  color: #9333ea;
  font-size: 12px;
  font-family: monospace;
`;

const ControlsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
`;

const ControlButton = styled.button<{ $variant?: 'danger' | 'primary' | 'secondary' }>`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${props => 
    props.$variant === 'danger' ? 'rgba(239, 68, 68, 0.5)' :
    props.$variant === 'primary' ? 'rgba(30, 58, 138, 0.5)' :
    'rgba(255, 255, 255, 0.3)'
  };
  background: ${props =>
    props.$variant === 'danger' ? 'rgba(252, 165, 165, 0.3)' :
    props.$variant === 'primary' ? 'rgba(196, 181, 253, 0.5)' :
    'rgba(233, 213, 255, 0.5)'
  };
  color: #4c1d95;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    transform: scale(1.05);
    background: ${props =>
      props.$variant === 'danger' ? 'rgba(252, 165, 165, 0.5)' :
      props.$variant === 'primary' ? 'rgba(196, 181, 253, 0.7)' :
      'rgba(233, 213, 255, 0.7)'
    };
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorContainer = styled.div`
  background: rgba(252, 165, 165, 0.3);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`;

const ErrorText = styled.span`
  color: #991b1b;
  font-size: 14px;
`;

const RetryButton = styled.button`
  background: rgba(99, 102, 241, 0.9);
  color: white;
  border: 1px solid rgba(99, 102, 241, 0.4);
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 8px;
  font-family: 'Inter', sans-serif;
  
  &:hover {
    background: rgba(79, 70, 229, 0.9);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  }
`;

const EndCallContainer = styled.div`
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%);
  backdrop-filter: blur(24px);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 20px;
  padding: 32px;
  text-align: center;
  max-width: 400px;
  width: 100%;
  margin: 0 auto;
  box-shadow: 0 20px 40px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2);
`;

const EndCallTitle = styled.h2`
  color: white;
  font-size: 24px;
  margin: 0 0 24px 0;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const EndCallButtons = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
`;

const ShareSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid rgba(147, 51, 234, 0.2);
`;

const ShareTitle = styled.h3`
  color: white;
  font-size: 18px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const ShareDescription = styled.div`
  color: white;
  font-size: 14px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const ShareButton = styled.button`
  padding: 12px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 52px;
  height: 52px;
  
  svg {
    width: 22px;
    height: 22px;
  }
  
  &:hover {
    background: rgba(233, 213, 255, 0.7);
    border-color: rgba(147, 51, 234, 0.5);
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 4px 12px rgba(147, 51, 234, 0.3);
  }
  
  &.x-twitter {
    background: #000000;
    border-color: #333333;
    color: white;
    
    &:hover {
      background: #1a1a1a;
      border-color: #444444;
      transform: translateY(-2px) scale(1.05);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    }
  }
  
  &.facebook {
    background: #1877f2;
    border-color: #1877f2;
    color: white;
    
    &:hover {
      background: #166fe5;
      border-color: #166fe5;
      transform: translateY(-2px) scale(1.05);
      box-shadow: 0 4px 12px rgba(24, 119, 242, 0.5);
    }
  }
  
  &.linkedin {
    background: #0a66c2;
    border-color: #0a66c2;
    color: white;
    
    &:hover {
      background: #095ba6;
      border-color: #095ba6;
      transform: translateY(-2px) scale(1.05);
      box-shadow: 0 4px 12px rgba(10, 102, 194, 0.5);
    }
  }
  
  &.email {
    background: #ea580c;
    border-color: #ea580c;
    color: white;
    
    &:hover {
      background: #dc2626;
      border-color: #dc2626;
      transform: translateY(-2px) scale(1.05);
      box-shadow: 0 4px 12px rgba(234, 88, 12, 0.5);
    }
  }
  
  
  &.copy {
    width: 100%;
    padding: 12px 20px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.12);
      border-color: rgba(255, 255, 255, 0.3);
    }
    
    &.success {
      background: rgba(16, 185, 129, 0.2);
      border-color: rgba(16, 185, 129, 0.4);
    }
  }
`;

const LearnMoreLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 20px;
  margin-top: 16px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.25);
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ShareButtonsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
`;


const EndCallButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 12px 32px;
  background: ${props => 
    props.$variant === 'primary' 
      ? 'rgba(99, 102, 241, 0.9)'
      : 'rgba(255, 255, 255, 0.7)'
  };
  backdrop-filter: blur(20px);
  color: ${props => props.$variant === 'primary' ? 'white' : '#374151'};
  border: 1px solid ${props => 
    props.$variant === 'primary' 
      ? 'rgba(99, 102, 241, 0.4)'
      : 'rgba(255, 255, 255, 0.4)'
  };
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-shadow: ${props => 
    props.$variant === 'primary' 
      ? '0 1px 2px rgba(0, 0, 0, 0.3)'
      : '0 1px 2px rgba(255, 255, 255, 0.8)'
  };
  
  &:hover {
    background: ${props => 
      props.$variant === 'primary' 
        ? 'rgba(79, 70, 229, 0.9)'
        : 'rgba(255, 255, 255, 0.85)'
    };
    border-color: ${props => 
      props.$variant === 'primary' 
        ? 'rgba(79, 70, 229, 0.6)'
        : 'rgba(255, 255, 255, 0.6)'
    };
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(99, 102, 241, 0.3);
  }
`;

type CallState = 'idle' | 'connecting' | 'connected' | 'ended' | 'loading-recording' | 'recording-ready';

type ConversationEntry = {
  speaker: 'user' | 'agent';
  text: string;
  timestamp: Date;
};

interface CallViewProps {
  userData: {
    name: string;
    email: string;
  };
  podcastPrompt?: any;
  selectedHost: string;
  onCallEnd?: () => void;
}

const getAgentId = (hostName: string): string => {
  switch (hostName.toLowerCase()) {
    case 'tony':
      return 'agent_6605c1ef257221f24b292db665';
    case 'lisa':
      return 'agent_5bffafe18d2713f0376185a5b9';
    case 'sal':
      return 'agent_5e1d9c51e491cb9fb8efcd14e7'; // Updated Sal's agent ID
    default:
      return 'agent_c0f1412f9dca5de142c940c42d';
  }
};

export const CallView: React.FC<CallViewProps> = ({ userData, podcastPrompt, selectedHost, onCallEnd }) => {
  const [callState, setCallState] = useState<CallState>('idle');
  const [liveTranscript, setLiveTranscript] = useState('');
  const [liveTranscriptSpeaker, setLiveTranscriptSpeaker] = useState<'user' | 'agent'>('user');
  const [conversationHistory, setConversationHistory] = useState<ConversationEntry[]>([]);
  const [isAgentTalking, setIsAgentTalking] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionError, setConnectionError] = useState<boolean>(false);
  const [currentCallId, setCurrentCallId] = useState<string | null>(null);
  const [callDuration, setCallDuration] = useState(0);
  const [showRestartPrompt, setShowRestartPrompt] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [recordingData, setRecordingData] = useState<any>(null);
  const [, setRecordingError] = useState<string | null>(null);
  
  const retellWebClientRef = useRef<any>(null);
  const callStartTimeRef = useRef<Date | null>(null);
  const currentCallIdRef = useRef<string | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);


  // Initialize Retell client
  useEffect(() => {
    (async () => {
      try {
        const mod = await import('retell-client-js-sdk');
        const RetellWebClient = mod.RetellWebClient || (mod as any).default;
        retellWebClientRef.current = new RetellWebClient();
      } catch (e) {
        setError('Voice client unavailable');
      }
    })();

    return () => {
      if (retellWebClientRef.current) {
        try {
          retellWebClientRef.current.stopCall();
        } catch {}
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, []);

  // Update call duration timer
  useEffect(() => {
    if (callState === 'connected' && callStartTimeRef.current) {
      durationIntervalRef.current = setInterval(() => {
        const now = new Date();
        const duration = Math.floor((now.getTime() - callStartTimeRef.current!.getTime()) / 1000);
        setCallDuration(duration);
      }, 1000);
    } else {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    }
  }, [callState]);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const setupRetellEventListeners = () => {
    if (!retellWebClientRef.current) return;
    
    const client = retellWebClientRef.current;
    
    // Remove existing listeners
    client.removeAllListeners();
    
    // Call lifecycle events
    client.on('call_started', () => {
      setCallState('connected');
      callStartTimeRef.current = new Date();
    });
    
    client.on('call_ended', () => {
      const callId = currentCallIdRef.current || currentCallId;
      callStartTimeRef.current = null;
      setCallDuration(0);
      setShowRestartPrompt(false);
      
      // Go directly to recording page
      if (callId) {
        console.log('Call ended, transitioning to recording page with callId:', callId);
        window.location.hash = `recording-${callId}`;
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } else {
        console.error('No call ID available after call ended');
        setCallState('ended');
        setShowRestartPrompt(true);
      }
    });
    
    client.on('error', (error: any) => {
      setError(error.message || 'Call error occurred');
      setCallState('idle');
    });
    
    // Speaking status events
    client.on('agent_start_talking', () => {
      setIsAgentTalking(true);
      setIsUserSpeaking(false);
    });
    
    client.on('agent_stop_talking', () => {
      setIsAgentTalking(false);
    });
    
    client.on('user_start_talking', () => {
      setIsUserSpeaking(true);
      setIsAgentTalking(false);
    });
    
    client.on('user_stop_talking', () => {
      setIsUserSpeaking(false);
    });
    
    // Transcription updates
    client.on('update', (update: any) => {
      // Handle completed transcripts
      if (update.transcript && Array.isArray(update.transcript)) {
        const newHistory = update.transcript.map((utterance: any) => ({
          speaker: utterance.role as 'user' | 'agent',
          text: utterance.content,
          timestamp: new Date()
        }));
        setConversationHistory(newHistory);
      }
      
      // Handle real-time streaming
      if (update.user_transcript_delta) {
        setLiveTranscript(prev => prev + update.user_transcript_delta);
        setLiveTranscriptSpeaker('user');
      }
      
      if (update.agent_transcript_delta) {
        setLiveTranscript(prev => prev + update.agent_transcript_delta);
        setLiveTranscriptSpeaker('agent');
      }
      
      // Clear live transcript when utterance is complete
      if (update.transcript_complete) {
        setLiveTranscript('');
      }
    });
  };

  const startCall = async () => {
    try {
      setCallState('connecting');
      setError(null);
      
      // Track call start
      const agentId = getAgentId(selectedHost);
      analytics.trackCallStart(agentId, selectedHost);
      trackClick('start_call_button', 'call_action', { host: selectedHost });
      
      // Check microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      
      // Prepare dynamic variables for Retell LLM (using internal agents format)
      const retellLlmDynamicVariables = {
        caller_name: userData.name || 'Guest',
        caller_email: userData.email || '',
        caller_phone: '',
        selected_host: selectedHost,
        has_team_info: podcastPrompt?.team_info ? "true" : "false",
        // Add team info as a dynamic variable (already formatted as text)
        ...(podcastPrompt?.team_info && { team_info: podcastPrompt.team_info }),
        // Add any additional context based on user preferences
        ...((userData as any).favoriteTeam && { favorite_team: (userData as any).favoriteTeam }),
        ...((userData as any).leagueSize && { league_size: (userData as any).leagueSize }),
        ...((userData as any).experience && { fantasy_experience: (userData as any).experience }),
        // Add form data as context
        form_context: JSON.stringify(userData)
      };

      // Prepare synth_call_parameters for webhook context
      const synthCallParameters = {
        host_url: window.location.origin,
        tenant_id: '19', // or get from your auth context
        channel_id: 'web_call',
        channel_type: 'web_call',
        interaction_location: 'external_interaction',
        user_id: userData.email || 'anonymous',
        call_sid: 'pending', // Will be updated with actual Retell call_id
        synth_config_parameters: {
          caller_name: userData.name || 'Guest',
          caller_email: userData.email || '',
          caller_phone: '',
          selected_host: selectedHost,
          has_team_info: podcastPrompt?.team_info ? "true" : "false"
        },
        user_details: {
          name: userData.name || 'Guest',
          email: userData.email || '',
          phone: '',
          company: 'Fantasy Football User'
        },
        extra_messages: [
          {
            role: 'system',
            content: `User ${userData.name || 'Guest'} is calling about fantasy football. ${podcastPrompt?.team_info ? 'Team info available.' : 'No team info provided.'}`
          }
        ]
      };

      // Get access token (using minimal format like Retell tester)
      const response = await fetch('/api/retell/create-call', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent_id: agentId,
          retell_llm_dynamic_variables: {
            caller_name: userData.name || 'Guest',
            caller_email: userData.email || '',
            has_team_info: podcastPrompt?.team_info ? "true" : "false",
            selected_host: selectedHost,
            team_info: podcastPrompt?.team_info || ''
          },
          // metadata: {
          //   synth_conversation_id: `podcast_${Date.now()}`,
          //   is_training: false,
          //   is_testing: false,
          //   caller_name: userData.name,
          //   team_info: podcastPrompt?.team_info || null,
          //   form_data: userData
          // },
          // synth_call_parameters: synthCallParameters
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create call: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      const { access_token, call_id, mock, reason, details } = data;
      
      // Store call_id in both state and ref
      setCurrentCallId(call_id);
      currentCallIdRef.current = call_id;
      
      // Check if this is a mock response
      if (mock) {
        const errorMessage = reason || 'Demo mode: Voice calling requires Retell API configuration';
        setError(`Demo mode: ${errorMessage}${details ? ` (${details})` : ''}`);
        setCallState('idle');
        return;
      }
      
      // Setup event listeners
      setupRetellEventListeners();
      
      if (!retellWebClientRef.current) {
        throw new Error('Retell client not initialized');
      }
      
      if (!access_token) {
        throw new Error('No access token received from API');
      }
      
      // Start the call
      await retellWebClientRef.current.startCall({
        accessToken: access_token,
        sampleRate: 24000,
        enableUpdate: true
      });
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start call');
      setCallState('idle');
    }
  };

  const endCall = async () => {
    try {
      // Track call end
      const callDuration = callStartTimeRef.current ? (Date.now() - callStartTimeRef.current.getTime()) / 1000 : 0;
      analytics.trackCallEnd(callDuration, true);
      trackClick('end_call_button', 'call_action', { duration: callDuration });
      
      if (retellWebClientRef.current) {
        retellWebClientRef.current.stopCall();
      }
      
      setCallState('ended');
      setLiveTranscript('');
      callStartTimeRef.current = null;
      setCallDuration(0);
      setShowRestartPrompt(true);
      // Don't clear currentCallId here - wait for recording flow to complete
    } catch (err) {
      // Silent error handling
    }
  };
  
  const handleRestartDecision = (restart: boolean) => {
    setShowRestartPrompt(false);
    if (restart) {
      window.location.reload();
    } else if (onCallEnd) {
      onCallEnd();
    }
  };

  const toggleMute = () => {
    if (retellWebClientRef.current) {
      if (isMuted) {
        retellWebClientRef.current.unmute();
      } else {
        retellWebClientRef.current.mute();
      }
      setIsMuted(!isMuted);
    }
  };

  const copyToClipboard = async () => {
    const shareText = `Check out this amazing Fantasy Football AI Expert! Talk to AI experts about your fantasy league. Try it at: ${window.location.href}`;
    
    try {
      await navigator.clipboard.writeText(shareText);
      setCopySuccess(true);
      trackClick('copy_link_callview', 'share', { 
        call_state: callState,
        button_context: 'callview_container',
        action: 'copy_share_link'
      });
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      analytics.trackError('copy_failed', String(err), { 
        call_state: callState,
        button_context: 'callview_container'
      });
    }
  };
  
  const shareOnX = () => {
    const location = callState === 'ended' ? 'after_call' : 'during_call';
    trackSocialShare('x', location);
    trackClick('share_x_callview', 'social', { 
      call_state: callState, 
      share_platform: 'x',
      share_location: location,
      button_context: 'callview_container'
    });
    const text = `Yobi just studied your fantasy team… should we tell you the results? 👀`;
    const url = window.location.href;
    window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };
  
  const shareOnFacebook = () => {
    const location = callState === 'ended' ? 'after_call' : 'during_call';
    trackSocialShare('facebook', location);
    trackClick('share_facebook_callview', 'social', { 
      call_state: callState, 
      share_platform: 'facebook',
      share_location: location,
      button_context: 'callview_container'
    });
    const url = window.location.href;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
  };
  
  const shareOnLinkedIn = () => {
    const location = callState === 'ended' ? 'after_call' : 'during_call';
    trackSocialShare('linkedin', location);
    trackClick('share_linkedin_callview', 'social', { 
      call_state: callState, 
      share_platform: 'linkedin',
      share_location: location,
      button_context: 'callview_container'
    });
    const url = window.location.href;
    const title = 'Yobi AI Fantasy Football Expert';
    const summary = 'Just tried out Yobi\'s new AI Fantasy Football expert tool. It gave me a whole new perspective on my roster. Listen to my recording here:';
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(summary)}`, '_blank');
  };
  
  const shareViaEmail = () => {
    const location = callState === 'ended' ? 'after_call' : 'during_call';
    trackSocialShare('email', location);
    trackClick('share_email_callview', 'social', { 
      call_state: callState, 
      share_platform: 'email',
      share_location: location,
      button_context: 'callview_container'
    });
    const subject = 'Check out this Fantasy Football AI Expert!';
    const body = `I just discovered this amazing tool that connects you with Fantasy Football AI experts!\n\nYou can have AI experts analyze your league matchups, give analysis, and more.\n\nTry it here: ${window.location.href}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleRecordingReady = (url: string, data: any) => {
    setRecordingUrl(url);
    setRecordingData(data);
    setCallState('recording-ready');
  };

  const handleRecordingError = (error: string) => {
    setRecordingError(error);
    setCallState('ended'); // Fall back to ended state
    setShowRestartPrompt(true);
  };

  const handleNewCall = () => {
    // Reset all states for new call
    setCallState('idle');
    setCurrentCallId(null);
    currentCallIdRef.current = null;
    setRecordingUrl(null);
    setRecordingData(null);
    setRecordingError(null);
    setShowRestartPrompt(false);
    setLiveTranscript('');
    setConversationHistory([]);
    setError(null);
    setConnectionError(false);
    window.location.hash = '';
  };

  // Auto-start call when component mounts
  useEffect(() => {
    startCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container>
      {showRestartPrompt ? (
        <EndCallContainer>
          <EndCallTitle>Start new Podcast?</EndCallTitle>
          <EndCallButtons>
            <EndCallButton 
              $variant="primary" 
              onClick={() => handleRestartDecision(true)}
            >
              Yes
            </EndCallButton>
            <EndCallButton 
              $variant="secondary" 
              onClick={() => handleRestartDecision(false)}
            >
              No
            </EndCallButton>
          </EndCallButtons>
          
          <ShareSection>
            <ShareTitle>
              <Share2 size={20} />
              Share with Friends
            </ShareTitle>
            
            <ShareDescription>
              Love this Fantasy Football AI expert experience? Share it with your league!
            </ShareDescription>
            
            <ShareButtonsContainer>
              <ShareButton className="x-twitter" onClick={shareOnX} title="Share on X">
                <FontAwesomeIcon icon={faXTwitter} />
              </ShareButton>
              <ShareButton className="facebook" onClick={shareOnFacebook} title="Share on Facebook">
                <FontAwesomeIcon icon={faFacebook} />
              </ShareButton>
              <ShareButton className="linkedin" onClick={shareOnLinkedIn} title="Share on LinkedIn">
                <FontAwesomeIcon icon={faLinkedin} />
              </ShareButton>
              <ShareButton className="email" onClick={shareViaEmail} title="Share via Email">
                <FontAwesomeIcon icon={faEnvelope} />
              </ShareButton>
            </ShareButtonsContainer>
            
            <ShareButton 
              className={`copy ${copySuccess ? 'success' : ''}`}
              onClick={copyToClipboard}
              style={{ width: '100%', marginTop: '12px' }}
            >
              {copySuccess ? (
                <>
                  <CheckCircle size={16} />
                  Link Copied!
                </>
              ) : (
                <>
                  <Copy size={16} />
                  Copy Link to Share
                </>
              )}
            </ShareButton>

            <LearnMoreLink 
              href="https://yobi.com?utm_source=fantasy_football_app&utm_medium=referral&utm_campaign=fantasyfootball"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick('learn_more_yobi_callview', 'external_link', {
                utm_campaign: 'fantasyfootball',
                utm_source: 'fantasy_football_app',
                utm_medium: 'referral',
                link_context: 'callview_ended',
                call_state: callState
              })}
            >
              Learn more about Yobi →
            </LearnMoreLink>
          </ShareSection>
        </EndCallContainer>
      ) : (
        <>
          <CallContainer>
            <CallHeader>
              {callState === 'connecting' && (
                <>
                  <CallStatus>Connecting...</CallStatus>
                  <CallInfo>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)' }}>Creating your recording</span>
                  </CallInfo>
                </>
              )}
              
              {callState === 'connected' && (
                <>
                  <CallStatus>Call Connected</CallStatus>
                  <CallTimer>{formatDuration(callDuration)}</CallTimer>
                  {currentCallId && (
                    <CallInfo>
                      <CallId>Call ID: {currentCallId.slice(0, 8)}...</CallId>
                    </CallInfo>
                  )}
                </>
              )}
              
            </CallHeader>

        <ControlsContainer>
          {callState === 'connected' && (
            <>
              <ControlButton 
                $variant="secondary" 
                onClick={toggleMute}
                title={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? <MicOff size={24} /> : <Mic size={24} />}
              </ControlButton>
              <ControlButton 
                $variant="danger" 
                onClick={endCall}
                title="End Call"
              >
                <PhoneOff size={24} />
              </ControlButton>
            </>
          )}
          
          {callState === 'idle' && (
            <ControlButton 
              $variant="primary" 
              onClick={startCall}
              title="Start Call"
            >
              <Mic size={24} />
            </ControlButton>
          )}
        </ControlsContainer>

        {error && (
          <ErrorContainer>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertCircle size={16} />
              <ErrorText>{error}</ErrorText>
            </div>
            {connectionError && (
              <RetryButton onClick={() => {
                setConnectionError(false);
                setError(null);
                startCall();
              }}>
                Try Again
              </RetryButton>
            )}
          </ErrorContainer>
        )}
      </CallContainer>

      {callState === 'connected' && (
        <LiveTranscription
          conversationHistory={conversationHistory}
          liveTranscript={liveTranscript}
          liveTranscriptSpeaker={liveTranscriptSpeaker}
          isUserSpeaking={isUserSpeaking}
          isAgentTalking={isAgentTalking}
        />
      )}
      
        </>
      )}
      
      {/* Recording Loading State */}
      {callState === 'loading-recording' && currentCallId && (
        <RecordingLoadingPage
          callId={currentCallId}
          onRecordingReady={handleRecordingReady}
          onError={handleRecordingError}
        />
      )}
      
      {/* Recording Ready State */}
      {callState === 'recording-ready' && recordingUrl && recordingData && (
        <RecordingPage
          recordingUrl={recordingUrl}
          callData={recordingData}
          onNewCall={handleNewCall}
        />
      )}
    </Container>
  );
};
